import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney, formatDate, API_BASE, todayLocalIso, productSummary } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import InlineStatus from "@/components/InlineStatus";
import { ENTREGA } from "@/lib/statuses";
import { useAuth, hasRole } from "@/context/AuthContext";
import { toast } from "sonner";
import { Route as RouteIcon, Download, ExternalLink, Lock, CheckCircle2, ClipboardCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { ImprimirEtiquetas } from "@/components/domicilios/CargaEntrega";

const todayIso = todayLocalIso;

const RESULTADOS = [
  { value: "entregado", label: "Entregado", cls: "bg-blue-100 text-blue-900" },
  { value: "parcial", label: "Parcial", cls: "bg-yellow-100 text-yellow-900" },
  { value: "danado", label: "Dañado", cls: "bg-red-100 text-red-900" },
  { value: "otro_dia", label: "Otro día", cls: "bg-amber-100 text-amber-900" },
  { value: "no_contesto", label: "No contestó", cls: "bg-orange-100 text-orange-900" },
  { value: "fallido", label: "Fallido / Rechazado", cls: "bg-red-100 text-red-900" },
];

export default function ReporteRuta() {
  const { user } = useAuth();
  const [fecha, setFecha] = useState(todayIso());
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCierre, setShowCierre] = useState(false);
  const [resultados, setResultados] = useState({}); // { order_id: {resultado, nota} }
  const [pilotoNombre, setPilotoNombre] = useState("");
  const [repartidores, setRepartidores] = useState([]);
  const [assigning, setAssigning] = useState({}); // {orderId: bool}
  // Paginación cliente 30/página (los pedidos de un día suelen ser < 100).
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  // Cargar repartidores disponibles (usuarios rol piloto)
  useEffect(() => {
    api.get("/domicilios/repartidores").then(({ data }) => setRepartidores(data || [])).catch(() => {});
  }, []);

  const asignarRepartidor = async (orderId, userId) => {
    setAssigning((p) => ({ ...p, [orderId]: true }));
    try {
      await api.patch(`/orders/${orderId}/asignar-piloto`, { piloto_id: userId });
      toast.success("Repartidor asignado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAssigning((p) => ({ ...p, [orderId]: false }));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reporte-ruta", { params: { fecha } });
      setPedidos(data.pedidos || []);
      // Init resultados sin sobreescribir lo tecleado
      setResultados((prev) => {
        const next = { ...prev };
        for (const p of (data.pedidos || [])) {
          if (!next[p.id]) next[p.id] = { resultado: "", nota: "" };
        }
        return next;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  useEffect(() => { load(); }, [load]);

  const exportar = async () => {
    try {
      const token = localStorage.getItem("token");
      // Cache-buster para asegurar descarga fresca del backend
      const t = Date.now();
      const res = await fetch(`${API_BASE}/reporte-ruta.xlsx?fecha=${fecha}&_t=${t}`, {
        headers: token ? { Authorization: `Bearer ${token}`, "Cache-Control": "no-cache" } : { "Cache-Control": "no-cache" },
        cache: "no-store",
      });
      if (!res.ok) throw new Error("No se pudo exportar");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `reporte_ruta_${fecha}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success("Exportado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setResultado = (oid, patch) => {
    setResultados((prev) => ({ ...prev, [oid]: { ...(prev[oid] || {}), ...patch } }));
  };

  const missingCount = pedidos.filter((p) => !((resultados[p.id] || {}).resultado)).length;

  const cerrar = async () => {
    if (missingCount > 0) {
      return toast.error(`Faltan ${missingCount} pedido(s) sin resultado seleccionado`);
    }
    setBusy(true);
    try {
      const payload = {
        fecha,
        modulo: "ciudad",
        piloto_nombre: pilotoNombre,
        resultados: pedidos.map((p) => ({
          order_id: p.id,
          resultado: resultados[p.id].resultado,
          nota: resultados[p.id].nota || "",
        })),
      };
      const { data } = await api.post("/reporte-ruta/cierre", payload);
      toast.success(`Ruta cerrada · ${data.codigo}. Entregados: ${data.entregados} · No entregados: ${data.no_entregados}`);
      setShowCierre(false);
      setResultados({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const total = pedidos.reduce((acc, o) => acc + Number(o.total || 0), 0);
  const totalACobrar = pedidos.reduce((acc, o) => acc + (Number(o.total || 0) - Number(o.anticipo || 0)), 0);
  const entregadosCount = useMemo(
    () => Object.values(resultados).filter((r) => r?.resultado === "entregado").length,
    [resultados]
  );

  const canCerrar = hasRole(user, "admin") || hasRole(user, "facturacion");

  return (
    <div className="space-y-6" data-testid="reporte-ruta-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Ruta</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <RouteIcon className="w-8 h-8 text-[#10B981]" /> Reporte de Ruta
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Pedidos <b>“Se envía”</b> con destino <b>Entregas Ciudad</b> para el día seleccionado. Selecciona un resultado por pedido antes del cierre.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Fecha del reporte</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="rr-fecha" />
          </div>
          <Link to="/reporte-ruta/historial">
            <Button variant="outline" className="gap-2" data-testid="rr-historial-btn">
              <ClipboardCheck className="w-4 h-4" /> Historial de cierres
            </Button>
          </Link>
          <Button
            onClick={exportar}
            className="bg-[#002FA7] hover:bg-[#00227A] gap-2"
            disabled={pedidos.length === 0}
            data-testid="rr-export-btn"
          >
            <Download className="w-4 h-4" /> Exportar Excel
          </Button>
          {canCerrar && (
            <Button
              onClick={() => setShowCierre(true)}
              disabled={busy || pedidos.length === 0}
              className="bg-emerald-700 hover:bg-emerald-800 gap-2"
              data-testid="rr-cerrar-btn"
            >
              <Lock className="w-4 h-4" /> Cierre de ruta
            </Button>
          )}
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pedidos en ruta</div>
          <div className="font-manrope font-black text-3xl mt-1">{pedidos.length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</div>
          <div className="font-manrope font-black text-3xl mt-1 text-blue-700">{formatMoney(total)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">A cobrar</div>
          <div className="font-manrope font-black text-3xl mt-1 text-orange-700">{formatMoney(totalACobrar)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Resultados capturados</div>
          <div className="font-manrope font-black text-3xl mt-1 text-emerald-700">
            {pedidos.length - missingCount}/{pedidos.length}
          </div>
        </Card>
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-400">Cargando ruta...</div>
        ) : pedidos.length === 0 ? (
          <div className="p-8 text-center text-zinc-400">
            <RouteIcon className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
            No hay pedidos marcados <b>“Se envía”</b> para {fecha} (envío ciudad).<br />
            <span className="text-xs">Ve a <Link to="/pedidos" className="text-[#002FA7] hover:underline font-semibold">Hoja de Pedidos</Link> y marca los pedidos que enviarás hoy.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Pedido</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Cliente</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Productos</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Total</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">A cobrar</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Resultado</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Nota</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Estado actual</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Repartidor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {pedidos.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((o) => {
                  const total = Number(o.total || 0);
                  const ant = Number(o.anticipo || 0);
                  const r = resultados[o.id] || {};
                  const currentRes = RESULTADOS.find((x) => x.value === r.resultado);
                  return (
                    <tr key={o.id} className="align-top hover:bg-zinc-50" data-testid={`rr-row-${o.numero}`}>
                      <td className="px-3 py-2">
                        <Link to={`/pedidos/${o.id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline inline-flex items-center gap-1">
                          {o.numero} <ExternalLink className="w-3 h-3" />
                        </Link>
                        <div className="text-[10px] text-zinc-500 mt-1">{o.cliente_telefono}</div>
                        <ImprimirEtiquetas orderId={o.id} compact className="mt-1" />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-semibold">{o.cliente_nombre}</div>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[160px]" title={o.direccion}>{o.direccion}</div>
                      </td>
                      <td className="px-3 py-2 text-xs max-w-[320px]">
                        {(o.items || []).map((it, i) => (
                          <div key={i} className="text-[11px] leading-snug mb-0.5 uppercase tracking-tight" data-testid={`rr-item-summary-${o.numero}-${i}`}>
                            <b>{it.cantidad}×</b> {productSummary(it)}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-orange-700">{formatMoney(total - ant)}</td>
                      <td className="px-3 py-2 min-w-[160px]">
                        <Select value={r.resultado || ""} onValueChange={(v) => setResultado(o.id, { resultado: v })}>
                          <SelectTrigger className={`h-8 text-xs ${currentRes ? currentRes.cls : ""}`} data-testid={`rr-resultado-${o.numero}`}>
                            <SelectValue placeholder="Seleccionar…" />
                          </SelectTrigger>
                          <SelectContent>
                            {RESULTADOS.map((x) => (
                              <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 min-w-[160px]">
                        <Input
                          className="h-8 text-xs"
                          value={r.nota || ""}
                          onChange={(e) => setResultado(o.id, { nota: e.target.value })}
                          placeholder="Observación…"
                          data-testid={`rr-nota-${o.numero}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <InlineStatus
                          orderId={o.id}
                          campo="estado_entrega"
                          value={o.estado_entrega || "pendiente"}
                          options={ENTREGA}
                          canEdit={hasRole(user, "admin") || hasRole(user, "piloto") || hasRole(user, "facturacion")}
                          onChanged={load}
                        />
                      </td>
                      <td className="px-3 py-2 min-w-[170px]">
                        <Select
                          value={o.piloto_id || ""}
                          onValueChange={(v) => asignarRepartidor(o.id, v)}
                          disabled={assigning[o.id]}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`rr-repartidor-${o.numero}`}>
                            <SelectValue placeholder="Asignar repartidor…" />
                          </SelectTrigger>
                          <SelectContent>
                            {repartidores.length === 0 && (
                              <div className="p-2 text-xs text-zinc-500">Sin repartidores (crea usuarios rol piloto)</div>
                            )}
                            {repartidores.map((r) => (
                              <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pedidos.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-zinc-50" data-testid="rr-paginacion">
            <div className="text-xs text-zinc-600 tabular-nums">
              Mostrando <b>{((page - 1) * PAGE_SIZE) + 1}</b>–<b>{Math.min(page * PAGE_SIZE, pedidos.length)}</b> de <b>{pedidos.length}</b>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="rr-pag-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
              </Button>
              <span className="text-xs font-black tabular-nums">Pág. {page} / {Math.ceil(pedidos.length / PAGE_SIZE)}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(Math.ceil(pedidos.length / PAGE_SIZE), p + 1))} disabled={page >= Math.ceil(pedidos.length / PAGE_SIZE)} data-testid="rr-pag-next">
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={showCierre} onOpenChange={setShowCierre}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cierre de ruta · {formatDate(fecha)}</DialogTitle>
            <DialogDescription>
              Se cerrarán <b>{pedidos.length}</b> pedidos. Entregados: <b className="text-emerald-700">{entregadosCount}</b>. No entregados: <b className="text-orange-700">{pedidos.length - entregadosCount}</b>.
              {missingCount > 0 && (
                <div className="mt-2 text-red-600 text-xs font-semibold">Faltan {missingCount} pedido(s) sin resultado.</div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Piloto (opcional)</Label>
              <Input value={pilotoNombre} onChange={(e) => setPilotoNombre(e.target.value)} placeholder="Nombre del piloto" data-testid="cierre-piloto" />
            </div>
            <div className="bg-zinc-50 rounded p-3 text-[11px] space-y-1 text-zinc-600">
              <div>• Los pedidos <b>Entregados</b> se marcarán como entregados y saldrán de Seguimiento activo.</div>
              <div>• Los <b>No entregados</b> quedarán disponibles para volver a marcarse "Se envía" otro día.</div>
              <div>• El cierre queda en el <b>historial</b> como evidencia inmutable.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCierre(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={cerrar} disabled={busy || missingCount > 0} className="bg-emerald-700 hover:bg-emerald-800" data-testid="cierre-confirm">
              {busy ? "Cerrando..." : "Confirmar cierre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
